import { AxiosResponse } from 'axios';

import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';

export interface FedExParty {
  /** Contact person name → FedEx personName. */
  name?: string;
  /** Company / site name → FedEx companyName. */
  company?: string;
  phone?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  residential?: boolean;
}

export interface CompanyCourierAccount {
  id: string;
  account_number: string;
  description: string;
  is_default: boolean;
  sort_order: number;
}

export interface QuotePiece {
  weight: number;
  weightUnit?: 'LB' | 'KG';
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: 'IN' | 'CM';
  reference?: string;
}

/** One booked box in a ship response. */
export interface ShipResponsePiece {
  sequence: number;
  trackingNumber?: string | null;
  labelBase64?: string | null;
  labelContentType?: string | null;
  weight?: number | null;
}

export interface FedExQuotePayload {
  shipper: FedExParty;
  recipient: FedExParty;
  /** Flat single-package fields; omitted when `pieces` is sent. */
  weight?: number;
  weightUnit?: 'LB' | 'KG';
  /** 2 to 40 boxes; replaces the flat weight and dimension fields. */
  pieces?: QuotePiece[];
  shipDate?: string;
  dryIceKg?: number;
  /** Non-corrugated / irregular packaging → NON_STANDARD_CONTAINER. */
  nonStandardPackaging?: boolean;
  /** Lithium batteries (Section II ELB). Requires lithiumBatteryMaterial. */
  lithiumBatteries?: boolean;
  lithiumBatteryMaterial?: 'LITHIUM_ION' | 'LITHIUM_METAL' | 'BOTH';
  reference?: string;
  commodityDescription?: string;
  commodityValue?: number;
  /** Optional package dimensions in inches — sent only when all three are set. */
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: 'IN' | 'CM';
  /** Selected account number — sent to backend to override the default. */
  accountNumber?: string;
  /** Third-party FedEx account to bill (LIST rates only). When set, billing = THIRD_PARTY. */
  thirdPartyAccountNumber?: string;
  /** Postal code of the third-party account — required by FedEx for THIRD_PARTY billing. */
  thirdPartyPostalCode?: string;
  /** When true on quote, price as PRINT_RETURN_LABEL. */
  returnLabel?: boolean;
  /** Outbound order/reference for RMA barcode on return labels. */
  rmaReference?: string;
}

export interface FedExShipPayload extends FedExQuotePayload {
  serviceType: string;
  /** Label image format requested from FedEx. Defaults to PDF on the backend. */
  labelFormat?: 'PDF' | 'ZPLII';
  /** Proceed although the same booking was made recently or its outcome is unknown. */
  confirmDuplicate?: boolean;
}

export interface FedExRate {
  /** Unique within a quote response (serviceType alone can repeat). */
  rateId?: string;
  serviceType: string;
  serviceName?: string;
  serviceDescription?: string;
  currency?: string;
  totalNetCharge?: number;
  totalBaseCharge?: number;
  deliveryDate?: string | null;
  deliveryDay?: string | null;
  deliveryLabel?: string | null;
  transitTime?: string;
}

export interface FedExQuoteResponseData {
  rates: FedExRate[];
  raw?: unknown;
}

export interface FedExShipResponseData {
  trackingNumber?: string | null;
  masterTrackingNumber?: string | null;
  /** One entry per box when the shipment has more than one. */
  pieces?: ShipResponsePiece[];
  serviceType?: string | null;
  serviceCategory?: string | null;
  labelBase64?: string | null;
  labelContentType?: string | null;
  labelUrl?: string | null;
  labelFetchError?: string | null;
  isVirtualResponse?: boolean;
  alerts?: Array<{ code?: string; message?: string }>;
  shipDate?: string | null;
  deliveryDate?: string | null;
  deliveryDay?: string | null;
  transitTime?: string | null;
  raw?: unknown;
}

interface CustomResponseEnvelope<T> {
  data: T;
  is_success: boolean;
  message?: string;
  status?: number;
}

/**
 * ZoomLogi HTTP bodies use `return_class`: axios `data.data` is the CustomResponse
 * row, and the real payload is nested again at `data.data.data` (see backend
 * `accounts.utils.return_class`).
 */
export function unwrapFedExSuccessPayload<T extends object>(
  res: AxiosResponse<unknown>
): T | undefined {
  const body = res?.data as Record<string, unknown> | undefined;
  const envelope = body?.data as Record<string, unknown> | undefined;
  if (!envelope) return undefined;
  const nested = envelope.data;
  if (nested !== undefined && nested !== null && typeof nested === 'object') {
    return nested as T;
  }
  // The envelope itself is the payload when the backend does not nest it again.
  return envelope as T;
}

export function parseFedExQuoteResponse(
  res: AxiosResponse<unknown>
): FedExQuoteResponseData | undefined {
  const payload = unwrapFedExSuccessPayload<FedExQuoteResponseData>(res);
  if (payload?.rates && Array.isArray(payload.rates)) {
    return payload;
  }
  return undefined;
}

export function parseFedExShipResponse(
  res: AxiosResponse<unknown>
): FedExShipResponseData | undefined {
  const payload = unwrapFedExSuccessPayload<FedExShipResponseData>(res);
  if (!payload || typeof payload !== 'object') return undefined;
  return payload;
}

export const GetFedExAccounts = (
  token: string
): Promise<
  AxiosResponse<CustomResponseEnvelope<{ accounts: CompanyCourierAccount[] }>>
> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.fedexAccounts, token)
      .then((res) =>
        resolve(
          res as AxiosResponse<
            CustomResponseEnvelope<{ accounts: CompanyCourierAccount[] }>
          >
        )
      )
      .catch((error) => reject(error?.response));
  });
};

export const RequestFedExQuote = (
  token: string,
  payload: FedExQuotePayload
): Promise<AxiosResponse<CustomResponseEnvelope<FedExQuoteResponseData>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.fedexQuote, payload, token)
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<FedExQuoteResponseData>>
        )
      )
      .catch((error) => reject(error?.response));
  });
};

export const BookFedExShipment = (
  token: string,
  payload: FedExShipPayload,
  idempotencyKey?: string
): Promise<AxiosResponse<CustomResponseEnvelope<FedExShipResponseData>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.fedexShip,
        payload,
        token,
        undefined,
        undefined,
        // The drawer shows booking failures (409 duplicate, in progress, unknown) inline, so no global toast; a 401 still ends the session.
        'keep-auth',
        idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
      )
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<FedExShipResponseData>>
        )
      )
      // A request that got no response (timeout, dropped connection) has an unknown outcome.
      .catch((error) => reject(error?.response ?? error));
  });
};

export interface EmailLabelItem {
  trackingNumber: string;
  labelBase64: string;
  labelContentType: string;
}

export interface FedExEmailLabelPayload {
  to_email: string;
  label_base64?: string;
  /** One attachment per box; replaces `label_base64` for multi-box shipments. */
  labels?: EmailLabelItem[];
  tracking_number?: string;
  reference?: string;
  /** Defaults to image/png. Use application/x-zpl when Labelary PNG is unavailable. */
  content_type?: string;
}

export const EmailFedExLabel = (
  token: string,
  payload: FedExEmailLabelPayload
): Promise<AxiosResponse<CustomResponseEnvelope<{ sent: boolean }>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.fedexEmailLabel, payload, token)
      .then((res) =>
        resolve(res as AxiosResponse<CustomResponseEnvelope<{ sent: boolean }>>)
      )
      .catch((error) => reject(error?.response));
  });
};
